// src/pages/Menu.js
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { useCart } from '../context/CartContext';

const PageContainer = styled.div`
  max-width: 1180px;
  margin: 32px auto 56px;
  padding: 0 20px;

  @media (max-width: 700px) {
    margin: 18px auto 32px;
    padding: 0 10px;
  }
`;

/* ---------- NAV (desktop + mobile) ---------- */
const NavWrap = styled.div`
  position: relative;
  margin-bottom: 16px;
`;

/* Desktop tabs */
const TabBar = styled.div`
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-bottom: 18px;
  flex-wrap: wrap;

  @media (max-width: 860px) {
    display: none;
  }
`;

const Tab = styled.button`
  background: rgba(13,13,13,0.65);
  border: 1px solid rgba(225,214,192,0.25);
  border-radius: 999px;
  font-size: 1rem;
  color: #e1d6c0;
  padding: 9px 14px;
  cursor: pointer;
  text-decoration: none;
  &:hover { color: #ffffff; }
  &.active { border-color: #e1d6c0; color: #ffffff; background: rgba(225,214,192,0.12); }
`;

/* Mobile bar + dropdown */
const MobileBar = styled.div`
  display: none;
  align-items: center;
  justify-content: space-between;
  gap: 8px;

  @media (max-width: 860px) {
    display: flex;
  }
`;

const BrandLink = styled(Link)`
  color: #e1d6c0;
  text-decoration: none;
  font-weight: 700;
  font-size: 1.05rem;
`;

const MenuToggle = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: transparent;
  color: #e1d6c0;
  border: 1px solid #e1d6c0;
  border-radius: 10px;
  padding: 8px 12px;
  cursor: pointer;
`;

const DropMenu = styled.div`
  position: absolute;
  top: 42px;
  left: 0;
  right: 0;
  background: rgba(13,13,13,0.95);
  border: 1px solid #333;
  border-radius: 12px;
  padding: 8px;
  z-index: 10;
  display: ${p => (p.open ? 'block' : 'none')};

  @media (min-width: 861px) {
    display: none;
  }
`;

const DropItem = styled.button`
  width: 100%;
  text-align: left;
  background: transparent;
  border: none;
  color: #e1d6c0;
  padding: 10px 12px;
  border-radius: 8px;
  cursor: pointer;
  &:hover { background: rgba(255,255,255,0.06); color: #fff; }
`;

const DropLink = styled(Link)`
  display: block;
  color: #e1d6c0;
  padding: 10px 12px;
  border-radius: 8px;
  text-decoration: none;
  &:hover { background: rgba(255,255,255,0.06); color: #fff; }
`;

/* ---------- MENU BOOK ---------- */
const BookShell = styled.div`
  position: relative;
  max-width: 1100px;
  margin: 0 auto;
  overflow: hidden;
`;

const BookPage = styled.div`
  position: relative;
  min-height: 620px;
  background:
    linear-gradient(90deg, rgba(70,38,18,0.12), transparent 7%, transparent 93%, rgba(70,38,18,0.14)),
    linear-gradient(180deg, rgba(255,255,255,0.38), transparent 22%),
    #f4ead6;
  color: #1f1712;
  border: 1px solid #cdbd98;
  border-radius: 8px;
  box-shadow: 0 28px 70px rgba(0,0,0,0.45);
  padding: 34px;
  overflow: hidden;
  box-sizing: border-box;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    left: 50%;
    width: 28px;
    transform: translateX(-50%);
    background: linear-gradient(90deg, transparent, rgba(86,47,24,0.18), transparent);
    pointer-events: none;
  }

  @media (max-width: 700px) {
    min-height: 420px;
    padding: 18px 12px;
    border-radius: 6px;
    box-shadow: 0 16px 34px rgba(0,0,0,0.38);

    &::before {
      display: none;
    }
  }
`;

const PageTop = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 20px;
  border-bottom: 2px solid #7b251d;
  padding-bottom: 14px;
  margin-bottom: 24px;

  @media (max-width: 700px) {
    align-items: flex-start;
    flex-direction: column;
    gap: 8px;
    margin-bottom: 16px;
    padding-bottom: 10px;
  }
`;

const MenuTitle = styled.h1`
  margin: 0;
  color: #7b251d;
  font-size: clamp(2rem, 4vw, 3.5rem);
  font-family: Georgia, 'Times New Roman', serif;
  font-weight: 700;

  @media (max-width: 700px) {
    font-size: clamp(1.65rem, 9vw, 2.3rem);
  }
`;

const PageCount = styled.div`
  color: #6a5747;
  font-size: 0.95rem;
`;

const MenuGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 26px 34px;

  @media (max-width: 820px) {
    grid-template-columns: 1fr;
  }

  @media (max-width: 700px) {
    gap: 20px;
  }
`;

const CategoryBlock = styled.section`
  break-inside: avoid;
`;

const CategoryHeader = styled.h3`
  font-size: 1.25rem;
  margin: 0 0 12px;
  text-transform: uppercase;
  border-bottom: 1px solid #b99b63;
  padding-bottom: 7px;
  color: #7b251d;
  font-family: Georgia, 'Times New Roman', serif;

  @media (max-width: 700px) {
    font-size: 1.08rem;
  }
`;

const ItemList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const ItemRow = styled.li`
  display: grid;
  grid-template-columns: ${p => (p.hasImage ? '72px 1fr auto' : '1fr auto')};
  gap: 12px;
  align-items: start;
  padding: 10px 0;
  border-bottom: 1px dashed rgba(123,37,29,0.28);
  &:last-child { border-bottom: none; }

  @media (max-width: 520px) {
    grid-template-columns: ${p => (p.hasImage ? '58px minmax(0, 1fr)' : '1fr')};
    gap: 8px 10px;
    padding: 9px 0;
  }
`;

const ItemImage = styled.img`
  width: 72px;
  height: 72px;
  object-fit: cover;
  border-radius: 6px;
  border: 1px solid rgba(123,37,29,0.24);

  @media (max-width: 520px) {
    width: 58px;
    height: 58px;
  }
`;

const ItemMain = styled.div`
  min-width: 0;
`;

const ItemName = styled.span`
  display: block;
  color: #1f1712;
  font-weight: 700;
  line-height: 1.25;
  overflow-wrap: anywhere;
`;

const ItemPrice = styled.span`
  color: #7b251d;
  font-weight: 700;
  white-space: nowrap;
`;

const ItemActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;

  @media (max-width: 520px) {
    grid-column: ${p => (p.hasImage ? '2' : '1')};
    justify-content: flex-start;
    flex-wrap: wrap;
  }
`;

const AddButton = styled.button`
  background: #7b251d;
  color: #fff8ec;
  border: none;
  border-radius: 999px;
  font-weight: 700;
  padding: 6px 12px;
  cursor: pointer;
  &:hover { opacity: 0.85; }
`;

const AddedNote = styled.span`
  color: #1f6f3f;
  font-size: 0.9rem;
  font-weight: 700;
`;

const PageControls = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-top: 18px;

  @media (max-width: 520px) {
    gap: 10px;
  }
`;

const PageButton = styled.button`
  background: #e1d6c0;
  color: #0d0d0d;
  border: none;
  border-radius: 999px;
  padding: 10px 16px;
  font-weight: 700;
  cursor: pointer;
  opacity: ${p => (p.disabled ? 0.45 : 1)};
  pointer-events: ${p => (p.disabled ? 'none' : 'auto')};

  @media (max-width: 520px) {
    flex: 1;
    padding: 11px 10px;
    font-size: 0.95rem;
  }
`;

/* ---------- helpers ---------- */
const formatPrice = (p) => {
  if (typeof p === 'number') return `$${p.toFixed(2)}`;
  const cleaned = String(p).trim();
  if (/^\$/i.test(cleaned)) return cleaned;
  const num = parseFloat(cleaned);
  return Number.isFinite(num) ? `$${num.toFixed(2)}` : cleaned;
};

// case-insensitive match for “Drinks” page title
const isDrinksPage = (title) => /drink/i.test(title || '');

const isHandrollCategory = (category, pageTitle) =>
  /hand\s*roll|handroll/i.test(`${category || ''} ${pageTitle || ''}`);

const getOrderName = (itemName, category, pageTitle) => {
  if (!isHandrollCategory(category, pageTitle) || /hand\s*roll|handroll/i.test(itemName || '')) {
    return itemName;
  }
  return `${itemName} Handroll`;
};

const getItemImage = (item) => item.image_url || item.imageUrl || item.image || '';

/* ---------- component ---------- */
export default function Menu() {
  const [menuData, setMenuData] = useState([]);
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pageIndex, setPageIndex] = useState(0);
  const { addItem } = useCart();
  const [addedMap, setAddedMap] = useState({});

  // mobile menu state
  const [open, setOpen] = useState(false);

  const onAdd = (item, category, pageTitle) => {
    const orderName = getOrderName(item.name, category, pageTitle);
    addItem(orderName, item.price);
    setAddedMap((m) => ({ ...m, [orderName]: true }));
    setTimeout(() => setAddedMap((m) => ({ ...m, [orderName]: false })), 900);
  };

  useEffect(() => {
    Promise.all([
      fetch('/api/pages').then(r => r.json()),
      fetch('/api/menu').then(r => r.json())
    ]).then(([pagesData, menuData]) => {
      setPages(pagesData);
      setMenuData(menuData);
      setLoading(false);
    });
  }, []);

  // close dropdown on resize up to desktop
  useEffect(() => {
    const onResize = () => {
      if (window.innerWidth >= 860) setOpen(false);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  if (loading) return <PageContainer>Loading…</PageContainer>;

  const activeIndex = pageIndex;

  const getVisibleData = (idx) => {
    const page = pages[idx];
    return menuData.filter(cat =>
      page?.categories?.includes(cat.category)
    );
  };

  const turnToPage = (idx) => {
    if (idx === pageIndex || idx < 0 || idx >= pages.length) return;
    setPageIndex(idx);
  };

  const pickPage = (idx) => {
    turnToPage(idx);
    setOpen(false);
  };

  const renderMenuPage = (idx) => {
    const page = pages[idx];
    const visibleData = getVisibleData(idx);
    const hideAddButtons = isDrinksPage(page?.title);

    return (
      <BookPage>
        <PageTop>
          <MenuTitle>{page?.title || 'Menu'}</MenuTitle>
          <PageCount>Page {idx + 1} of {pages.length || 1}</PageCount>
        </PageTop>

        <MenuGrid>
          {visibleData.map(cat => (
            <CategoryBlock key={cat.category}>
              <CategoryHeader>{cat.category}</CategoryHeader>
              <ItemList>
                {cat.items.map(item => {
                  const image = getItemImage(item);
                  const orderName = getOrderName(item.name, cat.category, page?.title);
                  return (
                    <ItemRow key={item.name} hasImage={!!image}>
                      {image ? <ItemImage src={image} alt={item.name} loading="lazy" /> : null}
                      <ItemMain>
                        <ItemName>{item.name}</ItemName>
                      </ItemMain>
                      <ItemActions hasImage={!!image}>
                        <ItemPrice>{formatPrice(item.price)}</ItemPrice>
                        {!hideAddButtons && (
                          <>
                            <AddButton onClick={() => onAdd(item, cat.category, page?.title)}>Add</AddButton>
                            {addedMap[orderName] ? <AddedNote>Added</AddedNote> : null}
                          </>
                        )}
                      </ItemActions>
                    </ItemRow>
                  );
                })}
              </ItemList>
            </CategoryBlock>
          ))}
        </MenuGrid>
      </BookPage>
    );
  };

  return (
    <PageContainer>
      <NavWrap>
        {/* Desktop tabs */}
        <TabBar>
          <Tab as={Link} to="/">Home</Tab>
          {pages.map((p, idx) => (
            <Tab
              key={p.id}
              onClick={() => turnToPage(idx)}
              className={idx === activeIndex ? 'active' : ''}
            >
              {p.title}
            </Tab>
          ))}
        </TabBar>

        {/* Mobile hamburger */}
        <MobileBar>
          <BrandLink to="/" onClick={() => setOpen(false)}>Home</BrandLink>
          <MenuToggle
            aria-label="Toggle menu"
            aria-expanded={open}
            onClick={() => setOpen(v => !v)}
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
              <path d="M4 7h16M4 12h16M4 17h16" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            Menu
          </MenuToggle>
          <DropMenu open={open} onClick={() => setOpen(false)}>
            <DropLink to="/">Home</DropLink>
            {pages.map((p, idx) => (
              <DropItem key={p.id} onClick={(e) => { e.stopPropagation(); pickPage(idx); }}>
                {idx === activeIndex ? '• ' : ''}{p.title}
              </DropItem>
            ))}
          </DropMenu>
        </MobileBar>
      </NavWrap>

      <BookShell>
        {renderMenuPage(pageIndex)}
      </BookShell>

      <PageControls>
        <PageButton
          onClick={() => turnToPage(pageIndex - 1)}
          disabled={pageIndex === 0}
        >
          Previous Page
        </PageButton>
        <PageButton
          onClick={() => turnToPage(pageIndex + 1)}
          disabled={pageIndex >= pages.length - 1}
        >
          Next Page
        </PageButton>
      </PageControls>
    </PageContainer>
  );
}
